import {
  Alert,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  UnorderedList,
  useDisclosure
} from '@chakra-ui/react'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type {
  BaseParamsDto,
  InsuranceParamsDto,
  PolicyHolderDto
} from 'BrokerTsLib/src/dtos'
import {
  InitialBaseParamsValues,
  InitialGeneralParamsValues,
  InitialPolicyHolderValues,
  InitialVehicleValues
} from 'BrokerTsLib/src/dtos'
import { DummyStep1 } from 'brokertslib/src/dtos/DummyStep1'
import type {
  VehiclesIdentificationDto,
  VinOnlineVehicleDto
} from 'brokertslib/src/dtos/VinOnline'
import {
  FindVehicle
} from 'brokertslib/src/dtos/VinOnline'
import {
  Insurer,
  RiskVariant,
  Selection,
  SelectionType,
} from 'brokertslib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { Controller, FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import {
  GeneralParamsValidationSchema,
  isOtherMake,
  IsOtherNonregistered,
  IsTrailer,
  PolicyHolderValidationSchema,
  VehicleBaseParamsValidationSchema,
  VehicleValidationSchema,
  znackaRequiredTypes,
} from 'brokertslib/src/validation'
import _ from 'lodash'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'

import { CheckboxFormControl, DateFormControl, SwitchFormControl } from 'BrokerTsLib/src/components'
import { SourceTypes } from 'BrokerTsLib/src/enums/SourceTypes'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { startOfDay } from 'date-fns'
import { Datepicker } from '../../components/Datepicker'
import ErrorSummary from '../../components/ErrorSummary'
import { FormGroup } from '../../components/FormGroup'
import { FormLabel } from '../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { FormSpacer } from '../../components/FormSpacer'
import { InputGroup } from '../../components/InputGroup'
import { InsuranceCompanies } from '../../components/InsuranceCompanies'
import { MainContent } from '../../components/MainContent'
import { Panel } from '../../components/Panel'
import { RadioStack } from '../../components/RadioStack'
import { VehicleEvaluation } from '../../components/VehicleEvaluation'
import { useInsurers, useSelections, useYearKm } from '../../hooks'
import { useApp } from '../../hooks/useApp'
import { getVehicleIdentificationLink } from '../../hooks/useDocuments'
import { useNameof } from '../../hooks/useNameof'
import {
  useGetMakers,
  useGetModels,
  useGetModelsBase,
  useVehicleIdentification,
} from '../../hooks/useVehicle'
import {
  useGetStep1CategoryWarnings,
  useGetStep1Warnings
} from '../../hooks/useWizard'
import { Check, Cloud } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { BCNOptions } from '../BCNOptions'
import { CustomNumberInput } from '../CustomNumberInput'
import { FormButtons } from '../FormButtons'
import { ICOOptions } from '../ICOOptions'
import { InsureeAddressRuian } from '../InsureeAddressRuian'
import { InsureeName } from '../InsureeName'
import { InsureeType } from '../InsureeType'
import { PolicyHolderSameAsAcquirerWarning } from '../PolicyHolderSameAsAcquirerWarning'
import { ContractReplacement } from './ContractReplacement'
import { InsuranceType } from './InsuranceType'
import { PriceExcludeVatWarning } from './PriceExcludeVatWarning'
import {
  transformGeneralParamsDto,
  transformPolicyHolderDto,
  transformVehicleDto,
} from './Step1_transforms'
import { VehicleIdentificationForm } from './VehicleIdentificationForm'

interface Step1FormProps {
  onSubmit: (values: InsuranceParamsDto) => Promise<any>
  data?: InsuranceParamsDto
}

export const schema = yup.object().shape({
  baseParams: VehicleBaseParamsValidationSchema,
  generalParams: GeneralParamsValidationSchema,
  vehicle: VehicleValidationSchema,
  policyHolder: PolicyHolderValidationSchema,
})

const defaultValues: InsuranceParamsDto = {
  nextPage: 'next',
  baseParams: InitialBaseParamsValues,
  generalParams: InitialGeneralParamsValues,
  policyHolder: InitialPolicyHolderValues,
  vehicle: InitialVehicleValues,
}

export const transformInsuranceParamsToFormValues = (
  values: InsuranceParamsDto,
) => {
  return {
    ...values,
    generalParams: transformGeneralParamsDto(values.generalParams),
    policyHolder: transformPolicyHolderDto(values.policyHolder),
    vehicle: transformVehicleDto(values.vehicle),
  }
}

export const Step1Form = ({
  onSubmit,
  data = defaultValues,
}: Step1FormProps) => {
  const { environment } = useApp()
  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const [vatWarningResetKey, setVatWarningResetKey] = useState(0)
  const {
    triggerNewInsurance,
    clearTriggerNewInsurance,
    insuranceParamsId,
    insuranceParams,
  } = useContext(insuranceParamsContext)
  const insuranceParamsVehicle = insuranceParams as InsuranceParamsDto

  const formValues = useMemo(() => transformInsuranceParamsToFormValues(data), [data])
  useEffect(() => {
    methods.reset(formValues)
  }, [formValues])

  const isContracted = !!formValues.generalParams.contractNumber

  const nameof = useNameof<InsuranceParamsDto>()
  const phnameof = useNameof<PolicyHolderDto>(nameof((x) => x.policyHolder))
  const bpnameof = useNameof<BaseParamsDto>(nameof((x) => x.baseParams))

  const {
    data: dataPf,
    isLoading: isLoadingPf,
    isFetching: isFetchingPf,
  } = useSelections(SelectionType.FrekvencePlatby)
  const {
    data: dataPt,
    isLoading: isLoadingPt,
    isFetching: isFetchingPt,
  } = useSelections(SelectionType.ZpusobPlatby)

  const {
    data: dataPalivo,
    isLoading: isLoadingPalivo,
    isFetching: isFetchingPalivo,
  } = useSelections(SelectionType.Palivo)
  const { data: dataPuvod } = useSelections(SelectionType.PuvodVozidla)
  const { data: dataUzemniPlatnost } = useSelections(
    SelectionType.UzemniPlatnost,
  )
  const {
    data: dataCenaVozidlaZ,
    isLoading: isLoadingCenaVozidlaZ,
    isFetching: isFetchingCenaVozidlaZ,
  } = useSelections(SelectionType.CenaVozidlaZ)
  const {
    data: dataVybava,
    isLoading: isLoadingVybava,
    isFetching: isFetchingVybava,
  } = useSelections(SelectionType.Vybava)
  const {
    data: dataZabezpeceni,
    isLoading: isLoadingZabezpeceni,
    isFetching: isFetchingZabezpeceni,
  } = useSelections(SelectionType.Zabezpeceni)
  const {
    data: dataUsage,
    isLoading: isLoadingUsage,
    isFetching: isFetchingUsage,
  } = useSelections(SelectionType.PouzitiVozidla)
  const {
    data: dataDruh,
    isLoading: isLoadingDruh,
    isFetching: isFetchingDruh,
  } = useSelections(SelectionType.DruhVozidla)
  const { data: dataNonregisteredVehicleSubcategory } = useSelections(
    SelectionType.NonregisteredVehicleSubcategory,
  )
  const {
    data: dataWarrantyLimitKm,
    isLoading: isLoadingWarrantyLimitKm,
    isFetching: isFetchingWarrantyLimitKm,
  } = useSelections(SelectionType.WarrantyLimitKm)
  const {
    data: dataWarrantyMonthLimit,
    isLoading: isLoadingWarrantyMonthLimit,
    isFetching: isFetchingWarrantyMonthLimit,
  } = useSelections(SelectionType.WarrantyMonthLimit)

  const {
    data: allInsurersData,
    isLoading: isLoadingInsurers,
    isFetching: isFetchingInsurers,
  } = useInsurers(false, null)
  const {
    data: allAddonInsurersData,
    isLoading: isLoadingAddonInsurers,
    isFetching: isFetchingAddonInsurers,
  } = useInsurers(true, null)

  const methods = useForm<InsuranceParamsDto>({
    defaultValues: formValues,
    resolver: yupResolver(schema) as any,
    mode: 'onBlur',
  })

  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    setError,
    watch,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = methods

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    if (triggerNewInsurance) {
      reset(transformInsuranceParamsToFormValues(defaultValues))
      setVatWarningResetKey(k => k + 1)
    }

    clearTriggerNewInsurance()
  }, [triggerNewInsurance])

  useEffect(() => {
    if (insuranceParams) {
      reset(
        transformInsuranceParamsToFormValues(
          insuranceParams as InsuranceParamsDto,
        ),
      )
      setVatWarningResetKey(k => k + 1)
    }
  }, [insuranceParams])

  // identifikace vozidla
  const {
    mutateAsync: getVehicleIdentification,
    isPending: isLoadingVehicleIdentification,
  } = useVehicleIdentification()
  const [vehicleIdentificationData, setVehicleIdentificationData] =
    useState<VehiclesIdentificationDto | null>(null)
  const [showVehicleEvaluation, setShowVehicleEvaluation] = useState(false)

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const povArray = watch('baseParams.pov') ?? []
  const pov = povArray.length > 0 ? povArray[0] : RiskVariant.WithoutPOV
  const havArray = watch('baseParams.hav') ?? []
  const hav = havArray.length > 0 ? havArray[0] : RiskVariant.WithoutHAV
  const spolArray = watch('baseParams.spol') ?? []
  const spol = spolArray.length > 0 ? spolArray[0] : undefined
  const holderSameAs = watch('policyHolder.holderSameAs')
  const ownerSameAs = watch('policyHolder.ownerSameAs')
  const phType = watch('policyHolder.person.type')
  const ownerType = watch('policyHolder.owner.type')
  const holderType = watch('policyHolder.holder.type')
  const priceType = watch('vehicle.priceType')
  const fuel = watch('vehicle.palivo')
  const category = watch('vehicle.druh')
  const maker = watch('vehicle.znacka')
  const modelBase = watch('vehicle.zakladniModel')
  const model = watch('vehicle.model')
  const puvod = watch('vehicle.puvod')
  const selectedInsurers = watch('baseParams.insurers') ?? []
  const selectedAddonInsurers = watch('baseParams.addonInsurers') ?? []
  const manufacturerWarranty = watch('vehicle.manufacturerWarrantyType')

  const showMakeText =
    category &&
    !isIn(parseInt(category.toString()), ...znackaRequiredTypes)
    &&
    isOtherMake(maker)

  const allInsurers = useMemo(() => [...(allInsurersData ?? [])], [
    allInsurersData,
    methods.formState.defaultValues,
  ])

  const allAddonInsurers = useMemo(() => [...(allAddonInsurersData ?? [])], [
    allAddonInsurersData,
    methods.formState.defaultValues
  ])
  const addonInsurersAvailable = allAddonInsurers.length > 0

  const {
    refetch: refetchMakers,
    data: makersData,
    isLoading: isLoadingMakers,
    isFetching: isFetchingMakers,
  } = useGetMakers(
    { fuelId: fuel ?? 0, categoryId: category ?? 0, useBrokerMakeModel: false },
    { enabled: false },
  )

  useEffect(() => {
    const data = makersData?.data
    if (data && !data.map((x: any) => x.toUpperCase()).includes(maker?.toUpperCase() ?? ''))
      setValue('vehicle.znacka', '')
  }, [makersData])

  const {
    refetch: refetchModelBase,
    data: modelBaseData,
    isLoading: isLoadingModelsBase,
    isFetching: isFetchingModelsBase,
  } = useGetModelsBase(
    { fuelId: fuel ?? 0, categoryId: category ?? 0, maker: maker ?? '', useBrokerMakeModel: false },
    { enabled: false },
  )

  useEffect(() => {
    const data = modelBaseData?.data
    if (data && !data.map((x: any) => x.toUpperCase()).includes(modelBase?.toUpperCase() ?? ''))
      setValue('vehicle.zakladniModel', '')
  }, [modelBaseData])

  const {
    refetch: refetchModels,
    data: modelsData,
    isLoading: isLoadingModels,
    isFetching: isFetchingModels,
  } = useGetModels(
    {
      fuelId: fuel ?? 0,
      categoryId: category ?? 0,
      maker: maker ?? '',
      modelBase: modelBase,
      useBrokerMakeModel: false
    },
    { enabled: false },
  )

  useEffect(() => {
    const data = modelsData?.data
    if (data && !data.map((x: any) => x.toUpperCase()).includes(model?.toUpperCase() ?? ''))
      setValue('vehicle.model', '')
  }, [modelsData])
  const isPrivesNaves = IsTrailer(category?.toString())
  const isOtherNonregistered = IsOtherNonregistered(category?.toString())
  useEffect(() => {
    refetchMakers().then(() => {
      if (maker) refetchModelBase().then(() => refetchModels())
    })
  }, [fuel, category])

  const setVehicleIndentificationLinks = (
    identificationData: VehiclesIdentificationDto,
  ) => {
    if (
      identificationData?.vehicles &&
      identificationData?.vehicles?.length > 0
    ) {
      const identParams = getVehicleIdentificationParams()
      for (let iv = 0; iv < identificationData.vehicles.length; iv++) {
        const vehicleElement = identificationData.vehicles[iv]
        if (vehicleElement.id)
          vehicleElement.pdfLink = getVehicleIdentificationLink({
            vehicleId: vehicleElement.id,
            spz: identParams.spz,
            vin: identParams.vin,
            paramsId: identParams.paramsId,
          })
      }
    }
  }

  const onCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue('vehicle.druh', e.target.value as any)
    setValue('vehicle.znacka', '')
    setValue('vehicle.znackaText', '')

    if (IsTrailer(e.target.value)) {
      setValue('vehicle.vykon1', null)
      setValue('vehicle.vykon2', null)
      setValue('vehicle.palivo', Selection.None.toString() as any)
      setValue('vehicle.objem', null)
      setValue('vehicle.pocetMistSezeni', null)
      setValue('vehicle.mileage', null)
    }

    if (IsOtherNonregistered(e.target.value)) {
      setValue('vehicle.objem', null)
      setValue('vehicle.pocetMistSezeni', null)
    }
  }

  const onMfctWarrantyTypeChange = (e: string) => {
    setValue('vehicle.manufacturerWarrantyType', parseInt(e))

    if (e == '1') {
      if (!getValues('vehicle.warrantyStartDate'))
        setValue(
          'vehicle.warrantyStartDate',
          getValues('vehicle.firstRegistrationDate'),
        )
    }
  }

  useEffect(() => {
    refetchModelBase()
    refetchModels()
  }, [maker?.toUpperCase()])
  useEffect(() => {
    refetchModels()
  }, [modelBase?.toUpperCase()])

  const { data: dataVehicleYearKm, isLoading: isLoadingVehicleYearKm, isFetching: isFetchingVehicleYearKm, } = useYearKm(category ?? 0, undefined, { enabled: !!category })

  const { data: dataWarnings, mutateAsync: getWarnings } = useGetStep1Warnings()
  const { data: dataCategoryWarnings, mutateAsync: getCategoryWarnings } =
    useGetStep1CategoryWarnings()

  const warnings =
    dataWarnings?.data?.filter(
      (x) =>
        (x.pov.length === 0 || x.pov.includes(pov?.toString() as any)) &&
        (x.hav.length === 0 ||
          (x.hav.includes(hav?.toString() ?? '') &&
            (x.spol.length === 0 || x.spol.includes(spol?.toString() ?? '')))),
    ) ?? []

  const categoryWarnings =
    dataCategoryWarnings?.data?.filter(
      (x) =>
        x.categories.length === 0 ||
        x.categories.includes(category?.toString() as any),
    ) ?? []

  useEffect(() => {
    getWarnings({ insurers: selectedInsurers })
    getCategoryWarnings({ insurers: selectedInsurers })
  }, [selectedInsurers])

  const ownerOptions = useMemo(
    () => [
      {
        label: 'Stejný s pojistníkem',
        value: Selection.PersonSameAsPolicyHolder,
      },
      {
        label: 'Stejný s držitelem/provozovatelem',
        value: Selection.PersonSameAsKeeper,
      },
      { label: 'Jiný', value: '' },
    ],
    [holderSameAs],
  )

  const fillDummyData = () => {
    const dummy = { ...DummyStep1 } // kopie globalniho objektu.

    dummy.baseParams = { ...dummy.baseParams, insurers: [1, 7] }
    dummy.vehicle = transformVehicleDto(DummyStep1.vehicle)
    dummy.generalParams = transformGeneralParamsDto(DummyStep1.generalParams)

    reset(dummy)
  }

  const getVehicleIdentificationParams = () => {
    return {
      paramsId: insuranceParamsId,
      spz: getValues('vehicle.licensePlate'),
      vin: getValues('vehicle.vin'),
      techCertNumber: getValues('vehicle.cisloTechnickehoPrukazu'),
    }
  }

  const submitForm = handleSubmit((data) => {
    const values = _.cloneDeep(data)

    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.vehicle.firstRegistrationDate = formatDate(values.vehicle.firstRegistrationDate)
    values.vehicle.firstRegistrationDateCz = formatDate(values.vehicle.firstRegistrationDateCz)
    values.generalParams.insuranceStartDate = formatDate(values.generalParams.insuranceStartDate)
    values.vehicle.purchaseDate = formatDate(values.vehicle.purchaseDate)
    values.policyHolder.person.birthday = formatDate(values.policyHolder.person.birthday)
    if (values.policyHolder.owner) values.policyHolder.owner.birthday = formatDate(values.policyHolder.owner.birthday)
    if (values.policyHolder.holder) values.policyHolder.holder.birthday = formatDate(values.policyHolder.holder.birthday)

    // ten jejich RadioIcon a select neumi s intem
    // TODO: Pouzit RadioGroupFormControl.
    values.vehicle.druh = values.vehicle.druh
      ? (parseInt(values.vehicle.druh.toString()) as any)
      : undefined
    values.vehicle.palivo = values.vehicle.palivo
      ? (parseInt(values.vehicle.palivo.toString()) as any)
      : undefined
    values.vehicle.usage = values.vehicle.usage
      ? (parseInt(values.vehicle.usage.toString()) as any)
      : undefined

    return onSubmit(values)
      .then(() => {
        if (values.nextPage == 'current') savedFormAlertOnOpen()
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
  })

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const onVehicleZnackaChange = (value: string) => {
    if (value !== 'OSTATNÍ') setValue('vehicle.znackaText', '')
  }

  const useVinonline = (vinOnlineData: VinOnlineVehicleDto) => {
    const veh = new FindVehicle(vinOnlineData)
    // pokud davam setValue cely objekt vehicle, tak se neprerendruje komponenta a nezmeni values - v UI to vypada dobre, ale uz se nespusti refetch apod.
    setValue('vehicle.licensePlate', veh.licensePlate)
    setValue('vehicle.vin', veh.vin)
    setValue('vehicle.palivo', veh.palivo)
    setValue('vehicle.puvod', veh.puvod)
    setValue('vehicle.objem', veh.objem)
    setValue('vehicle.vykon1', veh.vykon1)
    setValue('vehicle.pocetMistSezeni', veh.pocetMistSezeni)
    setValue('vehicle.celkovaHmotnost', veh.celkovaHmotnost)
    setValue('vehicle.cisloTechnickehoPrukazu', veh.cisloTechnickehoPrukazu)
    setValue('vehicle.firstRegistrationDate', veh.firstRegistrationDate)
    setValue('vehicle.firstRegistrationDateCz', veh.firstRegistrationDateCz)

    setValue('vehicle.znacka', veh.znacka)
    setValue('vehicle.druh', veh.druh)

    // bud se nastavuje model nebo zakladni model
    if (veh.model) {
      setValue('vehicle.zakladniModel', '')
      setValue('vehicle.model', veh.model)
    } else {
      setValue('vehicle.model', '')
      setValue('vehicle.zakladniModel', veh.zakladniModel)
    }
  }

  const minStartDate = startOfDay(new Date())

  // pref likvidaci zobrazujeme jen do 24.5.2025
  const showPreferredLiquidation = environment == 'production' && new Date().getTime() < new Date(2025, 4, 24).getTime()

  const price = watch('vehicle.price')
  const priceIncludeVat = watch('vehicle.priceIncludeVat')
  const lastVehicleValuationWithoutVat = useRef<number | null>(priceType === Selection.VehicleValuation ? priceIncludeVat ? Math.round((price ?? 0) / 1.21) : price : null)
  const onAfterPriceTypeChange = (pt: Selection) => {
    if (pt === Selection.VehicleValuation) {
      if (price) {
        const isDifferentToLastValuation = lastVehicleValuationWithoutVat.current === null ||
          (priceIncludeVat
            ? lastVehicleValuationWithoutVat.current !== Math.round(price / 1.21)
            : lastVehicleValuationWithoutVat.current !== price)
        if (isDifferentToLastValuation)
          setValue('vehicle.price', null)
      }
    }
  }
  
  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle display={'flex'} flexDir={'row'}>
                Parametry pojištění
                {environment == 'development' && (
                  <Text fontSize="md" paddingX={2} onClick={fillDummyData}>
                    *
                  </Text>
                )}
              </FormSectionTitle>

              <FormSectionDescription>
                Zadejte základní parametry pojištění.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <FormSpacer />

              {/* generalParams.name */}
              <FormControl
                isInvalid={!!errors.generalParams?.name}
                isDisabled={isContracted}
              >
                <FormLabel
                  htmlFor="generalParams.name"
                  tooltip="Zadejte název nabídky"
                >
                  Název nabídky
                </FormLabel>
                <InputGroup
                  id="generalParams.name"
                  placeholder="Zadejte název nabídky"
                  htmlSize={32}
                  errors={errors.generalParams?.name}
                  {...register('generalParams.name', {
                    required: 'Zadejte název nabídky',
                  })}
                />
              </FormControl>

              <FormSpacer />

              {/* Insurance type */}
              <InsuranceType
                isContracted={isContracted}
                propPath={nameof((x) => x.baseParams)}
              />

              <FormSpacer />

              <FormGroup size="lg">
                {/* generalParams.insuranceStartDate */}
                <DateFormControl control={control}
                  name={'generalParams.insuranceStartDate'}
                  label="Datum počátku pojištění"
                  labelProps={{
                    hint: 'Vyberte datum počátku pojištění. Zpětné pojištění není možné.',
                    hintType: 'okapps'
                  }}
                  isRequired
                  minDate={minStartDate}
                />

                {/* ContractAcceptationType */}
                {/* <FormControl>
                  <FormLabel>Způsob sjednání smlouvy</FormLabel>
                  <Controller
                    name="generalParams.contractAcceptationType"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup onChange={e => onChange(parseInt(e))} value={value?.toString() ?? ''} isDisabled={isContracted}>
                        <RadioStack variant="stackedTile">
                          {dataCat?.data.sort((x, y) => (y.displayOrder ?? 0) - (x.displayOrder ?? 0)).map((item) => (
                            <Radio value={item?.value?.toString() ?? ''} key={item.id}>
                              {item.description}
                            </Radio>
                          ))}
                        </RadioStack>
                      </RadioGroup>
                    )}
                  />
                </FormControl> */}
              </FormGroup>

              <FormSpacer />

              <ContractReplacement />

              <FormSpacer />

              <FormGroup size="lg">
                {/* generalParams.paymentFrequency */}
                <FormControl isRequired>
                  <Stack direction={'row'}>
                    <FormLabel
                      htmlFor="generalParams.paymentFrequency"
                      tooltip="Vyberte frekvenci plateb. Některé pojišťovny dávají přirážku za jinou než roční platbu."
                    >
                      Frekvence plateb
                    </FormLabel>

                    {(isFetchingPf || isLoadingPf) && <Spinner />}
                  </Stack>

                  <Controller
                    name="generalParams.paymentFrequency"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        onChange={(e) => onChange(parseInt(e))}
                        value={value?.toString() ?? ''}
                        isDisabled={isContracted}
                      >
                        <Stack direction="column" spacing={4}>
                          {dataPf
                            ?.filter((item) => item.id != Selection.OneTime)
                            .map((item) => (
                              <Radio
                                value={item.id.toString()}
                                key={item.id.toString()}
                              >
                                {item.description}
                              </Radio>
                            ))}
                        </Stack>
                      </RadioGroup>
                    )}
                  />
                </FormControl>

                {/* generalParams.paymentType */}
                <FormControl isRequired>
                  <Stack direction={'row'}>
                    <FormLabel
                      htmlFor="generalParams.paymentType"
                      tooltip="Vyberte způsob platby."
                    >
                      Způsob platby
                    </FormLabel>

                    {(isFetchingPt || isLoadingPt) && <Spinner />}
                  </Stack>

                  <Controller
                    name="generalParams.paymentType"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        onChange={(e) => onChange(parseInt(e))}
                        value={value?.toString() ?? ''}
                        isDisabled={isContracted}
                      >
                        <Stack direction="column" spacing={4}>
                          {dataPt?.map((item) => (
                            <Radio
                              value={item.id.toString()}
                              key={item.id.toString()}
                            >
                              {item.description}
                            </Radio>
                          ))}
                        </Stack>
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormControl isInvalid={!!errors.baseParams?.insurers}>
                <InsuranceCompanies
                  companies={allInsurers}
                  isLoadingCompanies={isFetchingInsurers || isLoadingInsurers}
                  propPath={bpnameof((x) => x.insurers)}
                  isDisabled={isContracted}
                  tooltip="Vyberte pojišťovny, které chcete zahrnout do kalkulace pojištění."
                />

                {errors.baseParams?.insurers && (
                  <FormErrorMessage>
                    {errors.baseParams.insurers.message}
                  </FormErrorMessage>
                )}
              </FormControl>

              {warnings.length > 0 && (
                <Alert status='warning'>
                  <Stack direction="column">
                    {warnings.map((item) => {
                      return (
                        <Text
                          key={item.message}
                          dangerouslySetInnerHTML={{ __html: item.message }}
                        />
                      )
                    })}
                  </Stack>
                </Alert>
              )}

              {addonInsurersAvailable && (
                <InsuranceCompanies
                  companies={allAddonInsurers}
                  title="Doplňkové pojišťovny"
                  isLoadingCompanies={
                    isFetchingAddonInsurers || isLoadingAddonInsurers
                  }
                  propPath={bpnameof((x) => x.addonInsurers)}
                  isDisabled={isContracted}
                  tooltip="Vyberte pojišťovny, které poskytují doplňková pojištění."
                />
              )}
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Informace o vozidle</FormSectionTitle>

              <FormSectionDescription>
                Využijte automatické vyplnění pomocí SPZ a čísla velkého
                technického průkazu nebo VIN čísla.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <VehicleIdentificationForm onEnterVinData={useVinonline} />

              <FormSpacer />

              {/* vehicle.druh */}
              {/* <FormControl>
                <FormLabel tooltip="Vyberte typ vozidla podle technického průkazu">
                  Druh vozidla
                </FormLabel>

                <RadioIconGroup control={control} name="vehicle.druh" isDisabled={isContracted}>
                  <RadioStack variant="tile">
                    <RadioIcon icon="car" value="17" {...register('vehicle.druh')}>
                      Osobní auto
                    </RadioIcon>
                    <RadioIcon icon="truck" value="18" {...register('vehicle.druh')}>
                      Užitkové vozidlo
                    </RadioIcon>
                    <RadioIcon icon="motobike" value="16" {...register('vehicle.druh')}>
                      Motocykl
                    </RadioIcon>
                    <RadioIcon icon="other" value="" {...register('vehicle.druh')}>
                      Ostatní
                    </RadioIcon>
                  </RadioStack>
                </RadioIconGroup>
              </FormControl> */}

              {/* vehicle.druh */}
              <FormControl sx={{ w: '280px' }} isRequired>
                <Stack direction={'row'}>
                  <FormLabel
                    htmlFor="vehicle.druh"
                    tooltip={<Box><b>Vyberte druh pojišťovaného vozidla</b> podle technického průkazu.
                      <List>
                        <ListItem><b>Užitkové</b> - nákladní vozidlo do 3,5t (dodávkové vozidlo, nebo-li VAN nebo nákladní vozidlo modifikováno z vozidla osobního)</ListItem>
                        <ListItem><b>Užitkové s rámem</b> - nákladní vozidlo do 3,5t, kde kabina řidiče a ostatních přepravovaných osob na straně jedné
                          a nákladní prostor na straně druhé jsou řešeny jako dva konstrukčně samostatné nebo oddělené celky
                          (např.kabina a za ní valník nebo nákladní skříň, případně technologická nástavba).</ListItem>
                      </List>
                    </Box>}
                  >
                    Druh vozidla
                  </FormLabel>
                  {(isFetchingDruh || isLoadingDruh) && <Spinner />}
                </Stack>
                <FormHelperText>
                  Vyberte druh vozidla podle technického průkazu
                </FormHelperText>

                {(dataDruh?.length ?? 0) > 0 && (
                  <Select
                    id="vehicle.druh"
                    {...register('vehicle.druh')}
                    isDisabled={isContracted}
                    onChange={(e) => onCategoryChange(e)}
                  >
                    <option value="" disabled>
                      Vyberte
                    </option>
                    {dataDruh?.map((item) => (
                      <option value={item.id.toString()} key={item.id}>
                        {item.description}
                      </option>
                    ))}
                  </Select>
                )}
              </FormControl>

              {categoryWarnings.length > 0 && (
                <Alert status='warning'>
                  <Stack direction="column">
                    {categoryWarnings.map((item) => {
                      return (
                        <Text
                          key={item.message}
                          dangerouslySetInnerHTML={{ __html: item.message }}
                        />
                      )
                    })}
                  </Stack>
                </Alert>
              )}

              <FormSpacer />

              <FormGroup>
                {/* vehicle.palivo */}
                {!isPrivesNaves && (
                  <FormControl sx={{ w: '280px' }} isRequired>
                    <Stack direction={'row'}>
                      <FormLabel
                        htmlFor="vehicle.palivo"
                        tooltip="Vyberte druh paliva podle technického průkazu."
                      >
                        Palivo
                      </FormLabel>
                      {(isFetchingPalivo || isLoadingPalivo) && <Spinner />}
                    </Stack>
                    <FormHelperText>
                      Vyberte druh paliva podle technického průkazu
                    </FormHelperText>

                    {(dataPalivo?.length ?? 0) > 0 && (
                      <Select
                        id="vehicle.palivo"
                        {...register('vehicle.palivo', {
                          onChange: (e) => {
                            if (e.target?.value && !Selection.isHybrid(e.target.value))
                              setValue('vehicle.vykon2', null)
                          }
                        })}
                        isDisabled={isContracted}
                      >
                        <option disabled>Vyberte</option>
                        {dataPalivo?.map((item) => (
                          <option value={item.id.toString()} key={item.id}>
                            {item.description}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}

                {isOtherNonregistered && (
                  <>
                    <FormControl sx={{ w: '280px' }} isRequired>
                      <FormLabel>Typ neregistrovaného vozidla</FormLabel>
                      <FormHelperText>&nbsp;</FormHelperText>

                      {(dataNonregisteredVehicleSubcategory?.length ?? 0) >
                        0 && (
                          <Select
                            id="vehicle.subCategory"
                            {...register('vehicle.subCategory')}
                            isDisabled={isContracted}
                          >
                            <option value="" disabled>
                              Vyberte
                            </option>
                            {dataNonregisteredVehicleSubcategory?.sort(
                              (it1, it2) =>
                                (it1.displayOrder ?? 0) -
                                (it2.displayOrder ?? 0),
                            )
                              .map((item) => (
                                <option value={item.id.toString()} key={item.id}>
                                  {item.description}
                                </option>
                              ))}
                          </Select>
                        )}

                      {errors.vehicle?.subCategory && (
                        <FormErrorMessage>
                          {errors.vehicle?.subCategory.message}
                        </FormErrorMessage>
                      )}
                    </FormControl>

                    <FormControl
                      sx={{ w: '280px' }}
                      isInvalid={!!errors.vehicle?.serialNumber}
                    >
                      <FormLabel>Výrobní číslo</FormLabel>
                      <FormHelperText>
                        Zadejte výrobní číslo vozidla pokud nemá VIN
                      </FormHelperText>
                      <InputGroup
                        isDisabled={isContracted}
                        autoComplete="off"
                        sx={{ width: '100%' }}
                        errors={errors.vehicle?.serialNumber}
                        {...register('vehicle.serialNumber')}
                      />
                      {errors.vehicle?.serialNumber && (
                        <FormErrorMessage>
                          {errors.vehicle.serialNumber.message}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  </>
                )}

                {/* vehicle.znacka */}
                <FormControl
                  sx={{ w: '280px' }}
                  isInvalid={!!errors.vehicle?.znacka}
                  isRequired
                >
                  <Stack direction={'row'}>
                    <FormLabel
                      htmlFor="vehicle.znacka"
                      tooltip="Vyberte tovární značku vozidla. V případě, že příslušná tovární značka není ve výběru, kontaktujte správce systému s žádostí o její přidání do číselníku."
                    >
                      Tovární značka
                    </FormLabel>
                    {(isLoadingMakers || isFetchingMakers) && <Spinner />}
                  </Stack>

                  <FormHelperText>
                    Zadejte značku podle technického průkazu
                  </FormHelperText>

                  {(makersData?.data.length ?? 0) > 0 && (
                    <Controller
                      name="vehicle.znacka"
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Select
                          id="vehicle.znacka"
                          onChange={(e) => {
                            onChange(e.target.value)
                            onVehicleZnackaChange(e.target.value)
                          }}
                          isDisabled={isContracted}
                          value={maker?.toUpperCase()}
                        >
                          <option disabled value="">
                            Vyberte značku vozidla
                          </option>
                          {makersData!.data.map((item) => (
                            <option value={item.toUpperCase()} key={item}>
                              {item.toUpperCase()}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  )}

                  {(makersData?.data.length ?? 0) == 0 && (
                    <Select isDisabled={true} defaultValue="">
                      <option disabled value="">
                        Vyberte značku
                      </option>
                    </Select>
                  )}
                </FormControl>

                {/* vehicle.znackaText */}
                {showMakeText && (
                  <FormControl isInvalid={!!errors.vehicle?.znackaText}>
                    <FormLabel htmlFor="vehicle.licensePlate">
                      Tovární značka (text)
                    </FormLabel>
                    <FormHelperText>
                      Zadejte tovární značku, pouze pokud nebyla vybrána ve
                      výběru
                    </FormHelperText>
                    <InputGroup
                      isDisabled={isContracted}
                      id="vehicle.znackaText"
                      autoComplete="off"
                      errors={errors.vehicle?.znackaText}
                      {...register('vehicle.znackaText')}
                    />
                  </FormControl>
                )}
              </FormGroup>

              <FormSpacer />

              <FormGroup>
                {/* vehicle.zakladniModel */}
                <FormControl isInvalid={!!errors.vehicle?.zakladniModel}>
                  <Stack direction={'row'}>
                    <FormLabel
                      htmlFor="vehicle.zakladniModel"
                      tooltip="Pomocné pole, které slouží pro snadnější identifikaci modelu vozidla."
                    >
                      Základní model
                    </FormLabel>

                    {(isLoadingModelsBase || isFetchingModelsBase) && (
                      <Spinner />
                    )}
                  </Stack>

                  {(modelBaseData?.data.length ?? 0) > 0 && (
                    <Select
                      id="vehicle.zakladniModel"
                      {...register('vehicle.zakladniModel')}
                      isDisabled={isContracted || !maker}
                    >
                      <option value="">Vyberte základní model</option>
                      {modelBaseData!.data.map((item) => (
                        <option value={item} key={item}>
                          {item}
                        </option>
                      ))}
                    </Select>
                  )}

                  {(modelBaseData?.data.length ?? 0) == 0 && (
                    <Select isDisabled={true} defaultValue="">
                      <option disabled value="">
                        Vyberte základní model
                      </option>
                    </Select>
                  )}
                </FormControl>

                {/* vehicle.model */}
                <FormControl
                  sx={{ w: '280px' }}
                  isInvalid={!!errors.vehicle?.model}
                  isRequired
                >
                  <Stack direction={'row'}>
                    <FormLabel
                      htmlFor="vehicle.model"
                      tooltip="Vyberte model vozidla. V případě, že příslušný model vozidla není ve výběru, kontaktujte správce systému s žádostí o jeho přidání do číselníku."
                    >
                      Model
                    </FormLabel>
                    {(isLoadingModels || isFetchingModels) && <Spinner />}
                  </Stack>

                  {(modelsData?.data.length ?? 0) > 0 && (
                    <Select
                      id="vehicle.model"
                      {...register('vehicle.model')}
                      isDisabled={isContracted}
                    >
                      <option disabled value="">
                        Vyberte model
                      </option>
                      {modelsData!.data.map((item) => (
                        <option value={item} key={item}>
                          {item}
                        </option>
                      ))}
                    </Select>
                  )}
                  {(modelsData?.data.length ?? 0) == 0 && (
                    <Select isDisabled={true} defaultValue="">
                      <option disabled value="">
                        Vyberte model
                      </option>
                    </Select>
                  )}
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormGroup>
                {/* vehicle.objem */}
                {!isPrivesNaves && (
                  <FormControl isInvalid={!!errors.vehicle?.objem} isRequired>
                    <FormLabel
                      htmlFor="volume"
                      tooltip="Zadejte zdvihový objem vozidla podle technického průkazu."
                    >
                      Zdvihový objem
                    </FormLabel>
                    <InputGroup
                      isDisabled={isContracted}
                      id="vehicle.objem"
                      placeholder="Např. 1589"
                      htmlSize={6}
                      autoComplete="off"
                      inputMode="decimal"
                      errors={errors.vehicle?.objem}
                      rightElement="㎤"
                      {...register('vehicle.objem')}
                    />
                  </FormControl>
                )}

                {/* vehicle.vykon1 */}
                {!isPrivesNaves && (
                  <FormControl isInvalid={!!errors.vehicle?.vykon1} isRequired>
                    <FormLabel
                      htmlFor="maxPerf"
                      tooltip="Zadejte maximální výkon vozidla podle technického průkazu."
                    >
                      Maximální výkon
                    </FormLabel>
                    <InputGroup
                      isDisabled={isContracted}
                      id="vehicle.vykon1"
                      placeholder="Např. 125"
                      htmlSize={6}
                      autoComplete="off"
                      inputMode="decimal"
                      errors={errors.vehicle?.vykon1}
                      rightElement="kW"
                      {...register('vehicle.vykon1')}
                    />
                  </FormControl>
                )}

                {/* vehicle.vykon2 */}
                {!isPrivesNaves &&
                  fuel && Selection.isHybrid(parseInt(fuel.toString())) && (
                    <FormControl isInvalid={!!errors.vehicle?.vykon2}>
                      <FormLabel
                        htmlFor="vehicle.vykon2"
                        tooltip="Zadejte maximální výkon druhého (elektrického) motoru podle technického průkazu."
                      >
                        Maximální výkon 2
                      </FormLabel>
                      <InputGroup
                        isDisabled={isContracted}
                        id="vehicle.vykon2"
                        placeholder="Např. 200"
                        htmlSize={6}
                        autoComplete="off"
                        inputMode="decimal"
                        errors={errors.vehicle?.vykon2}
                        rightElement="kW"
                        {...register('vehicle.vykon2')}
                      />
                    </FormControl>
                  )}

                {/* vehicle.celkovaHmotnost */}
                <FormControl
                  isInvalid={!!errors.vehicle?.celkovaHmotnost}
                  isRequired
                >
                  <FormLabel
                    htmlFor="weight"
                    tooltip="Zadejte největší technicky přípustnou hmotnost, příp. celkovou hmotnost podle technického průkazu."
                  >
                    {!isOtherNonregistered && (
                      <>Největší technicky přípustná hmotnost</>
                    )}
                    {isOtherNonregistered && <>Provozní hmotnost</>}
                  </FormLabel>
                  <InputGroup
                    isDisabled={isContracted}
                    id="vehicle.celkovaHmotnost"
                    placeholder="Např. 1600"
                    htmlSize={6}
                    autoComplete="off"
                    inputMode="decimal"
                    errors={errors.vehicle?.celkovaHmotnost}
                    rightElement="kg"
                    {...register('vehicle.celkovaHmotnost')}
                  />
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormGroup>
                {/* vehicle.puvod */}
                <FormControl isRequired>
                  <FormLabel
                    htmlFor="vehicle.puvod"
                    tooltip="Vyberte původ vozidla."
                  >
                    Původ vozidla
                  </FormLabel>

                  <RadioGroup
                    onChange={(e) => setValue('vehicle.puvod', parseInt(e))}
                    value={puvod?.toString() ?? ''}
                    isDisabled={isContracted}
                  >
                    <RadioStack variant="stackedTile">
                      {dataPuvod?.map((item) => (
                        <Radio value={item.id.toString()} key={item.id}>
                          {item.description}
                        </Radio>
                      ))}
                    </RadioStack>
                  </RadioGroup>
                </FormControl>

                {/* vehicle.uzemniPlatnost */}
                <FormControl isRequired>
                  <FormLabel
                    htmlFor="vehicle.uzemniPlatnost"
                    tooltip="Vyberte územní platnost pojištění."
                  >
                    Územní platnost
                  </FormLabel>

                  <Controller
                    name="vehicle.uzemniPlatnost"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        onChange={(e) => onChange(parseInt(e))}
                        value={value?.toString() ?? ''}
                        isDisabled={isContracted}
                      >
                        <RadioStack variant="stackedTile">
                          {dataUzemniPlatnost?.map((item) => (
                            <Radio value={item.id.toString()} key={item.id}>
                              {item.description}
                            </Radio>
                          ))}
                        </RadioStack>
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormGroup>
                {/* vehicle.pocetMistSezeni */}
                {!(isPrivesNaves || isOtherNonregistered) && (
                  <FormControl
                    isInvalid={!!errors.vehicle?.pocetMistSezeni}
                    isRequired
                  >
                    <FormLabel
                      htmlFor="seats"
                      tooltip="Zadejte počet míst k sezení podle technického průkazu."
                    >
                      Počet míst k sezení
                    </FormLabel>
                    <InputGroup
                      isDisabled={isContracted}
                      id="vehicle.pocetMistSezeni"
                      placeholder="Např. 5"
                      type="number"
                      htmlSize={6}
                      min="1"
                      max="200"
                      autoComplete="off"
                      inputMode="numeric"
                      errors={errors.vehicle?.pocetMistSezeni}
                      {...register('vehicle.pocetMistSezeni')}
                    />
                  </FormControl>
                )}

                {/* registrationDate */}
                <FormControl
                  isInvalid={!!errors.vehicle?.firstRegistrationDate}
                  isRequired
                >
                  <FormLabel
                    htmlFor="vehicle.firstRegistrationDate"
                    tooltip="Zadejte datum první registrace vozidla."
                  >
                    Datum první registrace
                  </FormLabel>

                  {isOtherNonregistered && (
                    <FormHelperText>
                      Pro neregistrovaná vozidla uveďte datum pořízení
                    </FormHelperText>
                  )}
                  <Datepicker
                    isDisabled={isContracted}
                    date={getValues('vehicle.firstRegistrationDate')}
                    errors={errors.vehicle?.firstRegistrationDate}
                    onDateChange={(date) =>
                      setValue('vehicle.firstRegistrationDate', date)
                    }
                  />
                </FormControl>

                {/* registrationDateCz */}
                {puvod &&
                  parseInt(puvod.toString()) ===
                  Selection.VehicleOriginForeign && (
                    <FormControl
                      isInvalid={!!errors.vehicle?.firstRegistrationDateCz}
                      isRequired
                    >
                      <FormLabel
                        htmlFor="vehicle.firstRegistrationDateCz"
                        tooltip="Zadejte datum první registrace vozidla v České republice."
                      >
                        Datum první registrace v ČR
                      </FormLabel>

                      <Datepicker
                        isDisabled={isContracted}
                        date={getValues('vehicle.firstRegistrationDateCz')}
                        errors={errors.vehicle?.firstRegistrationDateCz}
                        onDateChange={(date) =>
                          setValue('vehicle.firstRegistrationDateCz', date)
                        }
                      />
                    </FormControl>
                  )}

                {!isOtherNonregistered &&
                  selectedAddonInsurers &&
                  selectedAddonInsurers.indexOf(Insurer.Fortegra) >= 0 && (
                    <FormControl
                      isInvalid={!!errors.vehicle?.purchaseDate}
                      isRequired
                    >
                      <FormLabel
                        htmlFor="vehicle.purchaseDate"
                        tooltip="Zadejte datum nákupu vozidla."
                      >
                        Datum nákupu vozidla
                      </FormLabel>

                      <Datepicker
                        isDisabled={isContracted}
                        date={getValues('vehicle.purchaseDate')}
                        errors={errors.vehicle?.purchaseDate}
                        onDateChange={(date) =>
                          setValue('vehicle.purchaseDate', date)
                        }
                      />
                    </FormControl>
                  )}

                {/* vehicle.mileage */}
                {!(isPrivesNaves || isOtherNonregistered) && (
                  <FormControl isInvalid={!!errors.vehicle?.mileage} isRequired>
                    <FormLabel
                      htmlFor="vehicle.mileage"
                      tooltip="Zadejte stav tachometru"
                    >
                      Stav tachometru
                    </FormLabel>
                    <InputGroup
                      isDisabled={isContracted}
                      id="vehicle.mileage"
                      placeholder="Např. 68 000"
                      type="number"
                      htmlSize={12}
                      min="0"
                      max="99999999"
                      autoComplete="off"
                      inputMode="numeric"
                      errors={errors.vehicle?.mileage}
                      rightElement="km"
                      {...register('vehicle.mileage')}
                    />
                  </FormControl>
                )}
                {/* vehicle.yearKm */}
                {!isOtherNonregistered && (
                  <FormControl
                    sx={{ w: '280px' }}
                    isInvalid={!!errors.vehicle?.yearKm}
                    isRequired
                  >
                    <Stack direction={'row'}>
                      <FormLabel
                        htmlFor="vehicle.yearKm"
                        tooltip='Vyberte předpokládaný roční nájezd vozidla. Výběr má vliv na cenu u pojišťoven Pillow a Allianz. Pokud údaj neznáte vyberte možnost "nechci uvést".'
                      >
                        Roční nájezd
                      </FormLabel>
                      {(isFetchingVehicleYearKm || isLoadingVehicleYearKm) && (
                        <Spinner />
                      )}
                    </Stack>

                    {(dataVehicleYearKm?.length ?? 0) > 0 && (
                      <Select
                        id="vehicle.yearKm"
                        {...register('vehicle.yearKm')}
                        isDisabled={isContracted}
                      >
                        <option disabled value="">
                          Vyberte roční nájezd
                        </option>
                        {dataVehicleYearKm?.map((item) => (
                          <option
                            value={item.value?.toString() ?? ''}
                            key={item.value}
                          >
                            {item.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}

                {!isOtherNonregistered && (
                  <>
                    <FormControl>
                      <FormLabel tooltip="Klient souhlasí s opravou ve smluvním servisu pojistitele, za což dostává slevu na pojistném. V případě porušení závazku opravovat ve smluvním servise může pojistitel krátit pojistné plnění nebo navýšit spoluúčast.">
                        Souhlas s opravou ve sml. servisu
                      </FormLabel>

                      <Controller
                        name="vehicle.smluvniServis"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RadioGroup
                            onChange={(e) => onChange(e == '1')}
                            isDisabled={isContracted}
                            value={value ? '1' : '0'}
                          >
                            <RadioStack variant="stackedTile">
                              <Radio value={'1'}>Ano</Radio>
                              <Radio value={'0'}>Ne</Radio>
                            </RadioStack>
                          </RadioGroup>
                        )}
                      />
                    </FormControl>

                    {showPreferredLiquidation &&
                      <FormControl>
                        <FormLabel tooltip="Pouze pro pojišťovnu Kooperativa: V případě, kdy vozidlo nebude opraveno v určeném servisu, pojistitel poskytne pojistné plnění ve výši nákladů na opravu stanovených rozpočtem. Plnění na základě rozpočtu může pojištěný vyžádat i u vozidla mladšího než 120 měsíců. V případě, kdy sklo nebude opravováno v servisu určeném pojistitelem způsobem, jaký pojistitel určil, stanoví pojistitel pojistné plnění rozpočtem.">
                          Preferovaný způsob likvidace
                        </FormLabel>

                        <Controller
                          name="vehicle.prefLikvidace"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <RadioGroup
                              onChange={(e) => onChange(e == '1')}
                              isDisabled={isContracted}
                              value={value ? '1' : '0'}
                            >
                              <RadioStack variant="stackedTile">
                                <Radio value={'1'}>Ano</Radio>
                                <Radio value={'0'}>Ne</Radio>
                              </RadioStack>
                            </RadioGroup>
                          )}
                        />
                      </FormControl>
                    }

                    <FormControl>
                      <FormLabel tooltip="Pouze pro pojišťovnu HVP a Pillow: Vozidlo bude opravováno v autorizovaném servise. Pro Pillow: Pokud je označena volba Ano, pak cena pojištění je vždy bez slevy na smluvní servisy, klient může využít jakýkoli autorizovaný servis a není vázaný se seznam smluvních servisů uvedeném na webu pojišťovny.">
                        Autorizovaný servis
                      </FormLabel>

                      <Controller
                        name="vehicle.autorizovanyServis"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RadioGroup
                            onChange={(e) => onChange(e == '1')}
                            isDisabled={isContracted}
                            value={value ? '1' : '0'}
                          >
                            <RadioStack variant="stackedTile">
                              <Radio value={'1'}>Ano</Radio>
                              <Radio value={'0'}>Ne</Radio>
                            </RadioStack>
                          </RadioGroup>
                        )}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel tooltip="Pouze pro pojišťovnu UNIQA: Volbou Ano je nastaven servis bez omezení, kde pojišťovna počítá s obvyklými náklady v daném místě a čase na území na kterém se opravuje, ČR, SR i zahraničí. Volbou Ne pojišťovna počítá s obvyklými náklady ze servisů v ČR a SR. Pokud bude vozidlo opravováno v zahraničí, tak jsou použité náklady z ČR/SR a rozdíl doplácí pojištěný.">
                        Limit ceny prací servisu ze zahraničí
                      </FormLabel>

                      <Controller
                        name="vehicle.foreignRepairShop"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RadioGroup
                            onChange={(e) => onChange(e == '1')}
                            isDisabled={isContracted}
                            value={value ? '1' : '0'}
                          >
                            <RadioStack variant="stackedTile">
                              <Radio value={'1'}>Ano</Radio>
                              <Radio value={'0'}>Ne</Radio>
                            </RadioStack>
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </>
                )}
              </FormGroup>

              <FormSpacer />

              {/* Usage */}
              <FormControl isRequired>
                <FormLabel
                  htmlFor="usage"
                  tooltip="Vyberte použití vozidla. V případě jiného použití než „běžné“ se sjednává přirážka k pojistnému."
                >
                  Použití
                  {(isFetchingUsage || isLoadingUsage) && <Spinner />}
                </FormLabel>

                {(dataUsage?.length ?? 0) > 0 && (
                  <Select
                    id="vehicle.usage"
                    {...register('vehicle.usage')}
                    isDisabled={isContracted}
                  >
                    {dataUsage?.map((item) => (
                      <option value={item.id.toString()} key={item.id}>
                        {item.description}
                      </option>
                    ))}
                  </Select>
                )}
              </FormControl>

              <FormSpacer />

              {!isOtherNonregistered &&
                selectedAddonInsurers &&
                selectedAddonInsurers.indexOf(Insurer.Fortegra) >= 0 && (
                  <>
                    <FormGroup variant="price">
                      <FormControl isRequired>
                        <Stack direction={'row'}>
                          <FormLabel tooltip="Vyberte Ano, pokud je vozidlo v záruce výrobce.">
                            Vozidlo v záruce
                          </FormLabel>
                        </Stack>
                        <Controller
                          name="vehicle.manufacturerWarrantyType"
                          control={control}
                          render={({ field: { value } }) => (
                            <RadioGroup
                            /*onChange={e => onChange(parseInt(e))}*/ value={
                                value?.toString() ?? '0'
                              }
                              isDisabled={isContracted}
                              onChange={(e) => onMfctWarrantyTypeChange(e)}
                            >
                              <RadioStack variant="stackedTile">
                                <Radio value={'1'}>Ano</Radio>
                                <Radio value={'2'}>Ne</Radio>
                                <Radio value={'0'}>Nechci uvést</Radio>
                              </RadioStack>
                            </RadioGroup>
                          )}
                        />
                      </FormControl>
                    </FormGroup>

                    {manufacturerWarranty == 0 && (
                      <FormGroup>
                        <Text variant="warning">
                          Připojištění prodloužené záruky nebude kalkulováno
                        </Text>
                      </FormGroup>
                    )}

                    {manufacturerWarranty == 1 && (
                      <FormGroup>
                        <FormControl
                          isInvalid={!!errors.vehicle?.warrantyStartDate}
                          isRequired
                        >
                          <FormLabel
                            htmlFor="vehicle.warrantyStartDate"
                            tooltip="Zadejte datum počátku záruky vozidla."
                          >
                            Záruka od
                          </FormLabel>

                          <Datepicker
                            isDisabled={isContracted}
                            date={getValues('vehicle.warrantyStartDate')}
                            errors={errors.vehicle?.warrantyStartDate}
                            onDateChange={(date) =>
                              setValue('vehicle.warrantyStartDate', date)
                            }
                          />
                        </FormControl>

                        <FormControl
                          isInvalid={!!errors.vehicle?.warrantyTermLimit}
                          isRequired
                        >
                          <FormLabel
                            htmlFor="vehicle.warrantyTermLimit"
                            tooltip="Zadejte délku trvání záruky - počet měsíců"
                          >
                            Počet měsíců záruky
                          </FormLabel>
                          {(isFetchingWarrantyMonthLimit ||
                            isLoadingWarrantyMonthLimit) && <Spinner />}

                          {(dataWarrantyMonthLimit?.length ?? 0) > 0 && (
                            <Select
                              id="vehicle.warrantyTermLimit"
                              {...register('vehicle.warrantyTermLimit')}
                              isDisabled={isContracted}
                            >
                              <option value="" disabled>
                                Vyberte
                              </option>
                              {dataWarrantyMonthLimit?.map((item) => (
                                <option value={item.value} key={item.id}>
                                  {item.description}
                                </option>
                              ))}
                            </Select>
                          )}
                        </FormControl>

                        <FormControl
                          isInvalid={!!errors.vehicle?.warrantyKmLimit}
                          isRequired
                        >
                          <FormLabel
                            htmlFor="vehicle.warrantyKmLimit"
                            tooltip="Zadejte délku trvání záruky - počet km"
                          >
                            Počet km záruky
                          </FormLabel>
                          {(isFetchingWarrantyLimitKm ||
                            isLoadingWarrantyLimitKm) && <Spinner />}

                          {(dataWarrantyLimitKm?.length ?? 0) > 0 && (
                            <Select
                              id="vehicle.warrantyKmLimit"
                              {...register('vehicle.warrantyKmLimit')}
                              isDisabled={isContracted}
                            >
                              <option value="" disabled>
                                Vyberte
                              </option>
                              {dataWarrantyLimitKm?.filter((item) => item.name != 'NU')
                                .map((item) => (
                                  <option value={item.value} key={item.id}>
                                    {item.description}
                                  </option>
                                ))}
                            </Select>
                          )}
                        </FormControl>
                      </FormGroup>
                    )}
                  </>
                )}

              <FormGroup variant="price">
                {/* vehicle.priceType */}
                <FormControl>
                  <Stack direction={'row'}>
                    <FormLabel tooltip="Vyberte zdroj, ze kterého čerpáte cenu vozidla. V případě, že cenu neznáte, je možné vozidlo ocenit.">
                      Cena vozidla z
                    </FormLabel>
                    {(isFetchingCenaVozidlaZ || isLoadingCenaVozidlaZ) && (
                      <Spinner />
                    )}
                  </Stack>
                  <Controller
                    name="vehicle.priceType"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        onChange={(e) => {
                          onChange(parseInt(e))
                          onAfterPriceTypeChange(parseInt(e) as Selection)
                        }}
                        value={value?.toString() ?? ''}
                        isDisabled={isContracted}
                      >
                        <RadioStack variant="stackedTile">
                          {dataCenaVozidlaZ?.map((item) => (
                            <Radio value={item.id.toString()} key={item.id}>
                              {item.description}
                            </Radio>
                          ))}
                        </RadioStack>
                      </RadioGroup>
                    )}
                  />
                </FormControl>

                {priceType == Selection.VehicleValuation && !isContracted && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      // zvladuji se povinne inputy pro oceneni
                      setValue('vehicle.useVehicleEvaluation', true)
                      trigger([
                        'vehicle.firstRegistrationDate',
                        'vehicle.mileage',
                        'vehicle.licensePlate',
                        'vehicle.vin',
                      ]).then((valid) => {
                        if (!valid) return

                        setValue('vehicle.useVehicleEvaluation', false)
                        setShowVehicleEvaluation(false)

                        getVehicleIdentification(
                          getVehicleIdentificationParams(),
                        )
                          .then(({ data }) => {
                            setVehicleIndentificationLinks(data)
                            setVehicleIdentificationData(data)
                            setShowVehicleEvaluation(true)
                          })
                          .catch(() => {
                            setError('vehicle.vin', {
                              message: 'Nepodařilo se indentifikovat vozidlo',
                            })
                          })
                      })
                    }}
                    isLoading={isLoadingVehicleIdentification}
                  >
                    <Cloud color="secondary.500" /> Ocenit vozidlo
                  </Button>
                )}
              </FormGroup>

              {priceType == Selection.VehicleValuation && (
                <Stack direction="column">
                  {errors.vehicle?.licensePlate && (
                    <Text variant="error" fontSize={'sm'}>
                      {errors.vehicle.licensePlate.message}
                    </Text>
                  )}
                  {errors.vehicle?.vin && (
                    <Text variant="error" fontSize={'sm'}>
                      {errors.vehicle.vin.message}
                    </Text>
                  )}
                  {errors.vehicle?.mileage && (
                    <Text variant="error" fontSize={'sm'}>
                      {errors.vehicle.mileage.message}
                    </Text>
                  )}
                  {errors.vehicle?.firstRegistrationDate && (
                    <Text variant="error" fontSize={'sm'}>
                      {errors.vehicle.firstRegistrationDate.message}
                    </Text>
                  )}
                </Stack>
              )}

              {showVehicleEvaluation &&
                priceType == Selection.VehicleValuation &&
                !isContracted && (
                  <>
                    {!vehicleIdentificationData?.vehicles?.length && (
                      <Text variant="warning">
                        Vozidlo se nepodařilo identifikovat
                      </Text>
                    )}

                    {vehicleIdentificationData?.vehicles &&
                      vehicleIdentificationData?.vehicles?.length > 0 && (
                        <VehicleEvaluation
                          vehiclesIdentification={
                            vehicleIdentificationData ?? {}
                          }
                          onApplyEvaluation={({
                            price = null,
                            withTax = false,
                          }) => {
                            setShowVehicleEvaluation(false)
                            lastVehicleValuationWithoutVat.current = price
                            setValue('vehicle.priceIncludeVat', withTax)
                            setValue('vehicle.price', price)
                          }}
                        />
                      )}
                  </>
                )}

              <FormGroup alignItems='flex-start'>
                {/* Cena vozidla */}
                <FormControl isInvalid={!!errors.vehicle?.price}>
                  <FormLabel
                    htmlFor="vehicle.price"
                    tooltip="V případě, že je pojistníkem fyzická osoba zadejte částku vždy s DPH. V případě, že je klient právnická osoba nebo fyzická osoba podnikatel se v případě zadání částky bez DPH automaticky na čtvrtém kroku nastaví, že je klient plátcem DPH. Vždy si důkladně překontrolujte tyto údaje před sjednáním pojistné smlouvy."
                  >
                    Cena vozidla
                  </FormLabel>

                  {insuranceParams?.generalParams.source == SourceTypes.AutoRenewed &&
                    insuranceParamsVehicle.vehicle.origInsuranceParamsPrice &&
                    <FormHelperText>
                      Cena vozidla na původní smlouvě byla {insuranceParamsVehicle.vehicle.origInsuranceParamsPrice?.toLocaleString('cs-CZ', {
                        style: 'currency',
                        currency: 'CZK',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </FormHelperText>
                  }

                  <CustomNumberInput
                    w={40}
                    defaultValue={getValues('vehicle.price') ?? undefined}
                    onValueChange={(_, value) => {
                      setValue('vehicle.price', value ?? null)
                      if (priceType == Selection.VehicleValuation)
                        setValue(
                          'vehicle.priceType',
                          Selection.AccordingPolicyHolder,
                        )
                    }}
                    isDisabled={isContracted || priceType == Selection.VehicleValuation}
                    inputRightElement="Kč"
                    _placeholder="Např. 599 000"
                    min={0}
                    max={999999999}
                    inputProps={{
                      autoComplete: 'off',
                      step: 1,
                    }}
                  />

                  <FormErrorMessage>{errors.vehicle?.price?.message}</FormErrorMessage>
                </FormControl>

                {/* vehicle.priceIncludeVat */}
                <FormControl
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'end',
                    py: 1,
                  }}
                >
                  <FormLabel htmlFor="vehicle.priceIncludeVat" variant="switch">
                    bez DPH
                  </FormLabel>

                  <SwitchFormControl
                    name="vehicle.priceIncludeVat"
                    control={control}
                    onChange={(v) => {
                      const price = getValues('vehicle.price')
                      if (price) {
                        const priceInt = price ? parseInt(price!.toString()) : 0
                        const resultPrice = Math.round(
                          v ? priceInt * 1.21 : priceInt / 1.21,
                        )
                        setValue('vehicle.price', resultPrice)
                      }
                    }}
                    isDisabled={isContracted}
                  />

                  <FormLabel htmlFor="vehicle.priceIncludeVat" variant="switch">
                    s DPH
                  </FormLabel>
                </FormControl>

                <PriceExcludeVatWarning
                  key={vatWarningResetKey}
                  isDisabled={isContracted}
                  isNewInsurance={!insuranceParamsId}
                />
              </FormGroup>

              <FormSpacer />

              {/* Výbava */}
              {!isPrivesNaves && !isOtherNonregistered && (
                <FormControl w="100%">
                  <Stack direction={'row'}>
                    <FormLabel
                      htmlFor="vehicle.equipment"
                      tooltip="Vyberte dostupnou doplňkovou výbavu vozidla."
                    >
                      Výbava vozidla
                    </FormLabel>
                    {(isFetchingVybava || isLoadingVybava) && <Spinner />}
                  </Stack>

                  <Controller
                    name="vehicle.equipment"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CheckboxGroup
                        onChange={(values) =>
                          onChange(
                            values.map((val) => parseInt(val.toString())),
                          )
                        }
                        value={value ?? []}
                        isDisabled={isContracted}
                      >
                        <SimpleGrid
                          columns={{ sm: 2, md: 3 }}
                          spacingX="10"
                          spacingY="4"
                          w="100%"
                        >
                          {dataVybava?.filter(
                            (item) =>
                              [
                                `${Selection.AutTransmission}`,
                                `${Selection.FourWheelDrive}`,
                              ].some((x) => item.id.toString() === x) ?? true,
                          )
                            .map((item) => (
                              <Checkbox value={item.id} key={item.id}>
                                {item.description}
                              </Checkbox>
                            ))}
                        </SimpleGrid>
                      </CheckboxGroup>
                    )}
                  />
                </FormControl>
              )}

              <FormSpacer />

              {/* Zabezpečení */}
              {hav != RiskVariant.WithoutHAV && !isOtherNonregistered && (
                <FormControl w="100%">
                  <Stack direction={'row'}>
                    <FormLabel
                      htmlFor="vehicle.security"
                      tooltip="Vyberte zabezpečení vozidla."
                    >
                      Zabezpečení
                    </FormLabel>
                    {(isFetchingZabezpeceni || isLoadingZabezpeceni) && (
                      <Spinner />
                    )}
                  </Stack>

                  <Controller
                    name="vehicle.security"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CheckboxGroup
                        onChange={(values) =>
                          onChange(
                            values.map((val) => parseInt(val.toString())),
                          )
                        }
                        value={value ?? []}
                        isDisabled={isContracted}
                      >
                        <SimpleGrid
                          columns={{ sm: 2, md: 3 }}
                          spacingX="10"
                          spacingY="4"
                          w="100%"
                        >
                          {dataZabezpeceni?.map((item) => (
                            <Checkbox value={item.id} key={item.id}>
                              {item.description}
                            </Checkbox>
                          ))}
                        </SimpleGrid>
                      </CheckboxGroup>
                    )}
                  />
                </FormControl>
              )}

              <FormSpacer />

              {/* {hav != RiskVariant.WithoutHAV && false && (
                <FormControl>
                  <FormLabel tooltip="Zadejte mimořádnou výbavu vozidla. Možnost zadání mimořádné výbavy je pouze pro pojišťovny GČP a Kooperativa u ostatních pojišťoven nemá na nabídku žádný vliv.">
                    Mimořádná výbava
                  </FormLabel>

                  <FormHelperText>
                    Tento popis bude uveden ve smlouvě.
                  </FormHelperText>

                  <SpecialEquipment />
                </FormControl>
              )} */}
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Informace o pojistníkovi</FormSectionTitle>

              <FormSectionDescription>
                Zadejte informace o pojistníkovi.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <InsureeType personPrefix={phnameof((x) => x.person)} />

              <FormSpacer />

              {isIn(phType, Selection.Company, Selection.SelfEmployed) &&
                <CheckboxFormControl name='policyHolder.person.isVatPayer' control={control} isDisabled={isContracted} label='Plátce DPH' mb={4} />
              }

              {isIn(phType, Selection.Person, Selection.SelfEmployed) && (
                <BCNOptions personPrefix={phnameof((x) => x.person)} />
              )}

              {isIn(phType, Selection.Company, Selection.SelfEmployed) && (
                <ICOOptions personPrefix={phnameof((x) => x.person)} />
              )}

              <FormGroup>
                {isIn(phType, Selection.Person, Selection.SelfEmployed)
                  && (
                    <InsureeName
                      personPrefix={phnameof((x) => x.person)}
                      showTitles
                      isDisabled={isContracted}
                    />
                  )}
              </FormGroup>

              <FormSpacer />

              <InsureeAddressRuian
                addressPrefix={phnameof((x) => x.person.address)}
                isDisabled={isContracted}
              />

              {isIn(phType, Selection.Company, Selection.SelfEmployed) && (
                <>
                  <FormSpacer />

                  <Stack direction="column">
                    <FormLabel>
                      Splňuje pojistník alespoň 2 ze 3 níže uvedených podmínek?
                    </FormLabel>

                    <Box>
                      <UnorderedList spacing={1}>
                        <ListItem>
                          Je čistý obrat min. 13 600 000 EUR (cca 340 000 000
                          Kč)?
                        </ListItem>
                        <ListItem>
                          Je úhrn rozvahy min. 6 600 000 EUR (cca 165 000 000
                          Kč)?
                        </ListItem>
                        <ListItem>
                          Je průměrný roční stav zaměstnanců min. 250?
                        </ListItem>
                      </UnorderedList>
                    </Box>

                    <FormControl>
                      <Controller
                        name="policyHolder.bigRiskCompany"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RadioGroup
                            onChange={(e) => onChange(e == '1')}
                            isDisabled={isContracted}
                            value={value ? '1' : '0'}
                          >
                            <RadioStack variant="stackedTile">
                              <Radio value={'1'}>Ano</Radio>
                              <Radio value={'0'}>Ne</Radio>
                            </RadioStack>
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </Stack>
                </>
              )}

              <FormSpacer />

              {/* holder */}
              <FormControl isInvalid={!!errors.policyHolder?.holderSameAs}>
                <FormLabel tooltip="Zadejte držitele/provozovatele vozidla zapsaného v technickém průkazu.">
                  Držitel/provozovatel vozidla
                </FormLabel>

                <Controller
                  name="policyHolder.holderSameAs"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup
                      onChange={(e) => onChange(!e ? e : parseInt(e))}
                      value={value?.toString() ?? ''}
                      isDisabled={isContracted}
                    >
                      <RadioStack variant="stackedTile">
                        <Radio
                          value={Selection.PersonSameAsPolicyHolder.toString()}
                        >
                          Stejný s pojistníkem
                        </Radio>
                        <Radio value="">Jiný</Radio>
                      </RadioStack>
                    </RadioGroup>
                  )}
                />
              </FormControl>

              {!holderSameAs && (
                <Panel>
                  <InsureeType personPrefix={phnameof((x) => x.holder)} />

                  <FormSpacer />

                  {isIn(holderType, Selection.Company, Selection.SelfEmployed) && (
                    <CheckboxFormControl name='policyHolder.holder.isVatPayer' control={control} isDisabled={isContracted} label='Plátce DPH' mb={4} />
                  )}

                  {isIn(holderType, Selection.Person, Selection.SelfEmployed) &&
                    <BCNOptions personPrefix={phnameof((x) => x.holder)} />
                  }

                  {isIn(holderType, Selection.Company, Selection.SelfEmployed) &&
                    <ICOOptions personPrefix={phnameof((x) => x.holder)} />
                  }

                  <FormGroup>
                    {isIn(holderType, Selection.Person, Selection.SelfEmployed)
                      && (
                        <InsureeName
                          personPrefix={phnameof((x) => x.holder)}
                          showTitles
                          isDisabled={isContracted}
                        />
                      )}
                  </FormGroup>

                  <FormSpacer />

                  <InsureeAddressRuian
                    addressPrefix={phnameof((x) => x.holder!.address)}
                    isDisabled={isContracted}
                  />
                </Panel>
              )}

              {/* owner */}
              <FormControl isInvalid={!!errors.policyHolder?.ownerSameAs}>
                <FormLabel tooltip="Zadejte vlastníka vozidla zapsaného v technickém průkazu.">
                  Vlastník vozidla
                </FormLabel>

                <Controller
                  name="policyHolder.ownerSameAs"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup
                      onChange={(e) => onChange(!e ? e : parseInt(e))}
                      value={value?.toString() ?? ''}
                      isDisabled={isContracted}
                    >
                      <RadioStack variant="stackedTile">
                        {ownerOptions.map((item) => (
                          <Radio key={item.value} value={item.value.toString()}>
                            {item.label}
                          </Radio>
                        ))}
                      </RadioStack>
                    </RadioGroup>
                  )}
                />
              </FormControl>

              {!ownerSameAs && (
                <Panel>
                  <InsureeType personPrefix={phnameof((x) => x.owner)} />

                  <FormSpacer />

                  {isIn(ownerType, Selection.Company, Selection.SelfEmployed) &&
                    <CheckboxFormControl name='policyHolder.owner.isVatPayer' control={control} isDisabled={isContracted} label='Plátce DPH' mb={4} />
                  }

                  {isIn(ownerType, Selection.Person, Selection.SelfEmployed) &&
                    <BCNOptions personPrefix={phnameof((x) => x.owner)} />
                  }

                  {isIn(ownerType, Selection.Company, Selection.SelfEmployed) &&
                    <ICOOptions personPrefix={phnameof((x) => x.owner)} />
                  }

                  <FormGroup>
                    {isIn(ownerType, Selection.Person, Selection.SelfEmployed) && (
                      <InsureeName
                        personPrefix={phnameof((x) => x.owner)}
                        showTitles
                        isDisabled={isContracted}
                      />
                    )}
                  </FormGroup>

                  <FormSpacer />

                  <InsureeAddressRuian
                    addressPrefix={phnameof((x) => x.owner!.address)}
                    isDisabled={isContracted}
                  />

                  {isIn(ownerType, Selection.Company, Selection.SelfEmployed) && (
                    <FormControl>
                      <FormLabel>Vozidlo na leasing</FormLabel>

                      <Controller
                        name="vehicle.vehicleLeasing"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RadioGroup
                            onChange={(e) => onChange(e == '1')}
                            isDisabled={isContracted}
                            value={value ? '1' : '0'}
                          >
                            <RadioStack variant="stackedTile">
                              <Radio value={'1'}>Ano</Radio>
                              <Radio value={'0'}>Ne</Radio>
                            </RadioStack>
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  )}
                </Panel>
              )}
            </FormSectionBody>
            <PolicyHolderSameAsAcquirerWarning />
          </FormSection>

          <ErrorSummary errors={errors} />

          {!isContracted && (
            <FormButtons
              rightButtons={
                <>
                  <Button
                    variant="outline"
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}
                  >
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}
                  >
                    <Check />
                    Uložit a pokračovat
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header="Uloženo"
                    body={
                      !insuranceParamsId
                        ? 'Nabídka byla uložena do rozpracovaných kalkulací'
                        : 'Nabídka byla úspěšně aktualizována'
                    }
                  />
                </>
              }
            />
          )}
        </MainContent>
      </form>
    </FormProvider >
  )
}
